"use client";

import { c as _c } from "react/compiler-runtime";
import { EVENT_ATTACHMENT_MAX_COUNT, EVENT_ATTACHMENT_MAX_MB, type SdkApprovalEditableField, type SdkApprovalRequest, type SdkSubOpForDisplay } from "@bg-bgi/magnus-sdk";
import { Button } from "@workspace/ui/components/button";
import { Checkbox } from "@workspace/ui/components/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@workspace/ui/components/collapsible";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import { Textarea } from "@workspace/ui/components/textarea";
import { ChevronDown, Paperclip, X } from "lucide-react";
import { type Ref, useId, useRef, useState } from "react";
import type { ComposerUploadsSource } from "../attachments";
import { AttachFilesField, type AttachFilesHandle, type AttachFilesState } from "./attach-files-field";
export type ApprovalChoice = "always" | "approve-all" | "once" | "deny";

/** A chat-file reference inside an editable `attachments` arg. */
type AttachmentRef = {
  name?: string;
  outputFilename?: string;
  uploadId?: string;
};
function attachmentLabel(ref: AttachmentRef): string {
  return ref.name || ref.outputFilename || ref.uploadId || "attached file";
}
function readAttachmentRefs(value: unknown): AttachmentRef[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is AttachmentRef => typeof item === "object" && item !== null);
}
export interface ApprovalCopy {
  allowAlways?: string;
  allowOnce?: string;
  /** Label for the surface-wide bypass action ("yolo mode"). */
  approveAll?: string;
  /** Confirm-step destructive button label. */
  approveAllConfirmAction?: string;
  /** Confirm-step back button label. */
  approveAllConfirmBack?: string;
  /** Confirm-step body. Should spell out the blast radius. */
  approveAllConfirmDescription?: string;
  /** Confirm-step title shown before committing the surface-wide bypass. */
  approveAllConfirmTitle?: string;
  batchIntro?: string;
  deny?: string;
  description?: string;
  showDetails?: string;
  /** Verb prefix for single-tool summary, e.g. "Magnus wants to". */
  singleSummaryPrefix?: string;
  title?: string;
}
const DEFAULT_COPY: Required<ApprovalCopy> = {
  title: "Permission required",
  description: "Permission is required to perform this action.",
  singleSummaryPrefix: "Allow the following action:",
  batchIntro: "Run the following steps:",
  allowOnce: "Allow Once",
  allowAlways: "Always Allow",
  approveAll: "Approve everything…",
  approveAllConfirmTitle: "Approve everything?",
  approveAllConfirmDescription: "Every future action on this surface will run without asking — including ones that may cause permanent data loss. Actions marked as always requiring approval will still ask. You can turn this off in Settings at any time.",
  approveAllConfirmAction: "Yes, approve everything",
  approveAllConfirmBack: "Back",
  deny: "Deny",
  showDetails: "Show details"
};
function renderInline(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
}

/**
 * One editable field on the approval card. Controlled by the parent's
 * `edits` map so the final `editedArgs` diff is computed in one place.
 */
function EditableField(t0) {
  const $ = _c(41);
  const {
    field,
    value,
    disabled,
    onChange
  } = t0;
  const inputId = useId();
  if (field.kind === "boolean") {
    const t1 = Boolean(value);
    let t2;
    if ($[0] !== onChange) {
      t2 = v => onChange(v === true);
      $[0] = onChange;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== disabled || $[3] !== inputId || $[4] !== t1 || $[5] !== t2) {
      t3 = <Checkbox checked={t1} disabled={disabled} id={inputId} onCheckedChange={t2} />;
      $[2] = disabled;
      $[3] = inputId;
      $[4] = t1;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== field.label || $[8] !== inputId) {
      t4 = <Label className="font-normal" htmlFor={inputId}>{field.label}</Label>;
      $[7] = field.label;
      $[8] = inputId;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t3 || $[11] !== t4) {
      t5 = <div className="flex items-center gap-2">{t3}{t4}</div>;
      $[10] = t3;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  if (field.kind === "textarea") {
    let t1;
    if ($[13] !== field.label || $[14] !== inputId) {
      t1 = <Label htmlFor={inputId}>{field.label}</Label>;
      $[13] = field.label;
      $[14] = inputId;
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    let t2;
    if ($[16] !== onChange) {
      t2 = e => onChange(e.target.value);
      $[16] = onChange;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    const t3 = typeof value === "string" ? value : "";
    let t4;
    if ($[18] !== disabled || $[19] !== field.placeholder || $[20] !== inputId || $[21] !== t2 || $[22] !== t3) {
      t4 = <Textarea disabled={disabled} id={inputId} onChange={t2} placeholder={field.placeholder} rows={3} value={t3} />;
      $[18] = disabled;
      $[19] = field.placeholder;
      $[20] = inputId;
      $[21] = t2;
      $[22] = t3;
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    let t5;
    if ($[24] !== t1 || $[25] !== t4) {
      t5 = <div className="space-y-1.5">{t1}{t4}</div>;
      $[24] = t1;
      $[25] = t4;
      $[26] = t5;
    } else {
      t5 = $[26];
    }
    return t5;
  }
  let t1;
  if ($[27] !== field.label || $[28] !== inputId) {
    t1 = <Label htmlFor={inputId}>{field.label}</Label>;
    $[27] = field.label;
    $[28] = inputId;
    $[29] = t1;
  } else {
    t1 = $[29];
  }
  let t2;
  if ($[30] !== onChange) {
    t2 = e_0 => onChange(e_0.target.value);
    $[30] = onChange;
    $[31] = t2;
  } else {
    t2 = $[31];
  }
  const t3 = typeof value === "string" ? value : "";
  let t4;
  if ($[32] !== disabled || $[33] !== field.placeholder || $[34] !== inputId || $[35] !== t2 || $[36] !== t3) {
    t4 = <Input disabled={disabled} id={inputId} onChange={t2} placeholder={field.placeholder} value={t3} />;
    $[32] = disabled;
    $[33] = field.placeholder;
    $[34] = inputId;
    $[35] = t2;
    $[36] = t3;
    $[37] = t4;
  } else {
    t4 = $[37];
  }
  let t5;
  if ($[38] !== t1 || $[39] !== t4) {
    t5 = <div className="space-y-1.5">{t1}{t4}</div>;
    $[38] = t1;
    $[39] = t4;
    $[40] = t5;
  } else {
    t5 = $[40];
  }
  return t5;
}

/**
 * Editable `attachments` arg: the files the model already put on the call
 * render as removable chips; new files upload through the host's `uploads`
 * source (without one the existing list is remove-only).
 */
function EditableAttachments(t0) {
  const $ = _c(13);
  const {
    field,
    kept,
    disabled,
    onRemove,
    onUploadState,
    uploadHandleRef,
    uploads
  } = t0;
  const remainingSlots = Math.max(0, EVENT_ATTACHMENT_MAX_COUNT - kept.length);
  let t1;
  if ($[0] !== disabled || $[1] !== kept || $[2] !== onRemove) {
    t1 = kept.length > 0 && <ul className="space-y-1">{kept.map((ref, index) => <li className="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1 text-xs" key={`${attachmentLabel(ref)}-${ref.uploadId ?? ref.outputFilename ?? index}`}><Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" /><span className="min-w-0 flex-1 truncate">{attachmentLabel(ref)}</span><button aria-label={`Remove ${attachmentLabel(ref)}`} className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring" disabled={disabled} onClick={() => onRemove(index)} type="button"><X className="h-3 w-3" /></button></li>)}</ul>;
    $[0] = disabled;
    $[1] = kept;
    $[2] = onRemove;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== field || $[5] !== onUploadState || $[6] !== remainingSlots || $[7] !== uploadHandleRef || $[8] !== uploads) {
    t2 = uploads ? <AttachFilesField handleRef={uploadHandleRef} hint={`Sent to every attendee with the invitation. Max ${EVENT_ATTACHMENT_MAX_MB} MB each.`} label={field.label} maxFiles={remainingSlots} onStateChange={onUploadState} uploads={uploads} /> : null;
    $[4] = field;
    $[5] = onUploadState;
    $[6] = remainingSlots;
    $[7] = uploadHandleRef;
    $[8] = uploads;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t1 || $[11] !== t2) {
    t3 = <div className="space-y-1.5">{t1}{t2}</div>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}

/**
 * Input-mode approval card for the HITL gate. Rendered by the block in
 * place of the composer while a destructive tool call is paused waiting on
 * the user's decision (see `useApprovalPrompt`). Unlike the
 * multiple-choice picker, the decision is not a chat message: it resolves
 * the paused workflow through the host's `approvals` source (`onResolve`),
 * and the prompt is dismissed via the hook's resolved-id dedupe rather
 * than the conversation's message order.
 *
 * Tools may declare `editableFields` (e.g. createEvent's Title / Agenda /
 * Teams / Attachments): those args render as inputs pre-filled from the
 * call, the matching preview rows are suppressed, and approving sends only
 * the values the user actually changed as `editedArgs`.
 *
 * The component is keyed by `toolCallId` upstream so it always mounts fresh
 * for a new request.
 */
export function ApprovalInput(t0) {
  const $ = _c(96);
  const {
    request,
    onResolve,
    copy,
    disabled: t1,
    uploads
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const [showDetails, setShowDetails] = useState(false);
  const [confirmingApproveAll, setConfirmingApproveAll] = useState(false);
  let t2;
  if ($[0] !== copy) {
    t2 = {
      ...DEFAULT_COPY,
      ...copy
    };
    $[0] = copy;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const c = t2;
  const subOps = request.subOps;
  const isBatch = subOps.length > 0;
  const canPersistTrust = !request.alwaysRequireApproval;
  const editableFields = request.editableFields ?? [];
  const attachField = editableFields.find(_temp);
  const editableLabels = new Set(editableFields.map(_temp2));
  const previewRows = (request.previewFields ?? []).filter(row => !editableLabels.has(row.label));
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [edits, setEdits] = useState(t3);
  const [keptAttachments, setKeptAttachments] = useState(() => attachField ? readAttachmentRefs(request.args[attachField.key]) : []);
  const [removedAttachment, setRemovedAttachment] = useState(false);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      files: [],
      isUploading: false
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [uploadState, setUploadState] = useState(t4);
  const uploadHandleRef = useRef(null);
  let t5;
  if ($[4] !== request.args) {
    t5 = field => request.args[field.key] ?? field.absentValue;
    $[4] = request.args;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const baselineFor = t5;
  let t6;
  if ($[6] !== baselineFor || $[7] !== edits) {
    t6 = field_0 => field_0.key in edits ? edits[field_0.key] : baselineFor(field_0);
    $[6] = baselineFor;
    $[7] = edits;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const valueFor = t6;
  const buildEditedArgs = () => {
    const out = {};
    for (const field_1 of editableFields) {
      if (field_1.kind === "attachments") {
        const added = uploadState.files.flatMap(_temp3);
        if (removedAttachment || added.length > 0) {
          out[field_1.key] = [...keptAttachments, ...added];
        }
        continue;
      }
      if (field_1.key in edits && edits[field_1.key] !== baselineFor(field_1)) {
        out[field_1.key] = edits[field_1.key];
      }
    }
    return Object.keys(out).length > 0 ? out : undefined;
  };
  let t7;
  if ($[9] !== buildEditedArgs || $[10] !== onResolve) {
    t7 = choice => {
      if (choice === "deny") {
        uploadHandleRef.current?.discard();
        onResolve("deny");
        return;
      }
      uploadHandleRef.current?.reset();
      onResolve(choice, buildEditedArgs());
    };
    $[9] = buildEditedArgs;
    $[10] = onResolve;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const resolve = t7;
  const allowDisabled = disabled || uploadState.isUploading;
  if (confirmingApproveAll) {
    let t8;
    if ($[12] !== c.approveAllConfirmTitle) {
      t8 = <p className="font-medium text-foreground text-sm">{c.approveAllConfirmTitle}</p>;
      $[12] = c.approveAllConfirmTitle;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== c.approveAllConfirmDescription) {
      t9 = <p className="text-muted-foreground text-sm">{c.approveAllConfirmDescription}</p>;
      $[14] = c.approveAllConfirmDescription;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[16] !== t8 || $[17] !== t9) {
      t10 = <div className="space-y-1.5 px-4 pt-3.5 pb-2 text-center">{t8}{t9}</div>;
      $[16] = t8;
      $[17] = t9;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    let t11;
    if ($[19] !== resolve) {
      t11 = () => resolve("approve-all");
      $[19] = resolve;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    let t12;
    if ($[21] !== allowDisabled || $[22] !== c.approveAllConfirmAction || $[23] !== t11) {
      t12 = <Button className="w-full max-w-xs" disabled={allowDisabled} onClick={t11} type="button" variant="destructive">{c.approveAllConfirmAction}</Button>;
      $[21] = allowDisabled;
      $[22] = c.approveAllConfirmAction;
      $[23] = t11;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t13;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = () => setConfirmingApproveAll(false);
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let t14;
    if ($[26] !== c.approveAllConfirmBack) {
      t14 = <Button autoFocus={true} className="w-full max-w-xs" onClick={t13} type="button" variant="outline">{c.approveAllConfirmBack}</Button>;
      $[26] = c.approveAllConfirmBack;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    let t15;
    if ($[28] !== t12 || $[29] !== t14) {
      t15 = <div className="flex flex-col items-center gap-2 border-t border-border/60 px-3 py-3">{t12}{t14}</div>;
      $[28] = t12;
      $[29] = t14;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    let t16;
    if ($[31] !== t10 || $[32] !== t15) {
      t16 = <div className="w-full rounded-2xl border bg-composer-panel outline-none">{t10}{t15}</div>;
      $[31] = t10;
      $[32] = t15;
      $[33] = t16;
    } else {
      t16 = $[33];
    }
    return t16;
  }
  const t8 = "w-full rounded-2xl border bg-composer-panel outline-none";
  let t9;
  if ($[34] !== c.title) {
    t9 = <p className="font-medium text-foreground text-sm">{c.title}</p>;
    $[34] = c.title;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== c.description) {
    t10 = <p className="text-muted-foreground text-sm">{c.description}</p>;
    $[36] = c.description;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t9) {
    t11 = <div className="space-y-1.5 border-b border-border/60 px-4 pt-3.5 pb-3 text-center">{t9}{t10}</div>;
    $[38] = t10;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  const t12 = editableFields.length > 0 ? "max-h-[70vh] space-y-3 overflow-y-auto px-4 pt-3 pb-2 text-center text-sm" : "max-h-60 space-y-3 overflow-y-auto px-4 pt-3 pb-2 text-center text-sm";
  let t13;
  if ($[41] !== c.batchIntro || $[42] !== c.singleSummaryPrefix || $[43] !== isBatch || $[44] !== request.summary || $[45] !== subOps) {
    t13 = isBatch ? <div className="space-y-2"><p>{c.batchIntro}</p><ul className="max-h-40 list-disc space-y-1 overflow-y-auto rounded border py-1 pr-2 pl-7 text-left">{subOps.map(_temp4)}</ul></div> : <p dangerouslySetInnerHTML={{
      __html: `${c.singleSummaryPrefix} ${renderInline(request.summary)}.`
    }} />;
    $[41] = c.batchIntro;
    $[42] = c.singleSummaryPrefix;
    $[43] = isBatch;
    $[44] = request.summary;
    $[45] = subOps;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const t14 = previewRows.length > 0 && <dl className="mx-auto w-full max-w-sm rounded-lg border text-left text-xs">{previewRows.map(_temp5)}</dl>;
  const t15 = editableFields.length > 0 && <div className="mx-auto w-full max-w-sm space-y-3 text-left">{editableFields.map(field_3 => field_3.kind === "attachments" ? <EditableAttachments disabled={disabled} field={field_3} kept={keptAttachments} key={field_3.key} onRemove={index => {
      setRemovedAttachment(true);
      setKeptAttachments(prev => prev.filter((_, i_0) => i_0 !== index));
    }} onUploadState={setUploadState} uploadHandleRef={uploadHandleRef} uploads={uploads} /> : <EditableField disabled={disabled} field={field_3} key={field_3.key} onChange={value => setEdits(prev_0 => ({
      ...prev_0,
      [field_3.key]: value
    }))} value={valueFor(field_3)} />)}</div>;
  const t16 = `h-3 w-3 transition-transform ${showDetails ? "rotate-180" : ""}`;
  let t17;
  if ($[47] !== t16) {
    t17 = <ChevronDown className={t16} />;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== c.showDetails || $[50] !== t17) {
    t18 = <CollapsibleTrigger className="mx-auto flex items-center justify-center gap-1 text-muted-foreground text-xs hover:text-foreground">{t17}{c.showDetails}</CollapsibleTrigger>;
    $[49] = c.showDetails;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== request.args) {
    t19 = JSON.stringify(request.args, null, 2);
    $[52] = request.args;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== t19) {
    t20 = <CollapsibleContent><pre className="mt-2 max-h-48 overflow-auto rounded bg-muted p-2 text-left text-xs">{t19}</pre></CollapsibleContent>;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== showDetails || $[57] !== t18 || $[58] !== t20) {
    t21 = <Collapsible onOpenChange={setShowDetails} open={showDetails}>{t18}{t20}</Collapsible>;
    $[56] = showDetails;
    $[57] = t18;
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== t12 || $[61] !== t13 || $[62] !== t14 || $[63] !== t15 || $[64] !== t21) {
    t22 = <div className={t12}>{t13}{t14}{t15}{t21}</div>;
    $[60] = t12;
    $[61] = t13;
    $[62] = t14;
    $[63] = t15;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== resolve) {
    t23 = () => resolve("once");
    $[66] = resolve;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== allowDisabled || $[69] !== c.allowOnce || $[70] !== t23) {
    t24 = <Button autoFocus={true} className="w-full max-w-xs" disabled={allowDisabled} onClick={t23} type="button">{c.allowOnce}</Button>;
    $[68] = allowDisabled;
    $[69] = c.allowOnce;
    $[70] = t23;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== allowDisabled || $[73] !== c.allowAlways || $[74] !== canPersistTrust || $[75] !== resolve) {
    t25 = canPersistTrust && <Button className="w-full max-w-xs" disabled={allowDisabled} onClick={() => resolve("always")} type="button" variant="outline">{c.allowAlways}</Button>;
    $[72] = allowDisabled;
    $[73] = c.allowAlways;
    $[74] = canPersistTrust;
    $[75] = resolve;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] !== resolve) {
    t26 = () => resolve("deny");
    $[77] = resolve;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== c.deny || $[80] !== disabled || $[81] !== t26) {
    t27 = <Button className="w-full max-w-xs text-destructive hover:text-destructive" disabled={disabled} onClick={t26} type="button" variant="ghost">{c.deny}</Button>;
    $[79] = c.deny;
    $[80] = disabled;
    $[81] = t26;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== c.approveAll || $[84] !== canPersistTrust || $[85] !== disabled) {
    t28 = canPersistTrust && <Button className="w-full max-w-xs text-muted-foreground text-xs" disabled={disabled} onClick={() => setConfirmingApproveAll(true)} size="sm" type="button" variant="ghost">{c.approveAll}</Button>;
    $[83] = c.approveAll;
    $[84] = canPersistTrust;
    $[85] = disabled;
    $[86] = t28;
  } else {
    t28 = $[86];
  }
  let t29;
  if ($[87] !== t24 || $[88] !== t25 || $[89] !== t27 || $[90] !== t28) {
    t29 = <div className="flex flex-col items-center gap-2 px-3 py-3">{t24}{t25}{t27}{t28}</div>;
    $[87] = t24;
    $[88] = t25;
    $[89] = t27;
    $[90] = t28;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] !== t11 || $[93] !== t22 || $[94] !== t29) {
    t30 = <div className={t8}>{t11}{t22}{t29}</div>;
    $[92] = t11;
    $[93] = t22;
    $[94] = t29;
    $[95] = t30;
  } else {
    t30 = $[95];
  }
  return t30;
}
function _temp5(field_2) {
  return <div className="flex gap-3 border-border/60 border-b px-3 py-1.5 last:border-b-0" key={field_2.label}><dt className="w-20 shrink-0 text-muted-foreground">{field_2.label}</dt><dd className="min-w-0 break-words font-medium" dangerouslySetInnerHTML={{
      __html: renderInline(field_2.value)
    }} /></div>;
}
function _temp4(op, i) {
  const key = `${op.tool}-${i}-${op.summary}`;
  return <li dangerouslySetInnerHTML={{
    __html: renderInline(op.summary)
  }} key={key} />;
}
function _temp3(f_1) {
  return f_1.uploaded ? [{
    uploadId: f_1.uploaded.id,
    name: f_1.file.name
  }] : [];
}
function _temp2(f_0) {
  return f_0.label;
}
function _temp(f) {
  return f.kind === "attachments";
}